import React from 'react';
import { MousePointerClick, ShoppingCart } from 'lucide-react';
import styled from '../../styled';
import {
  AppMockupChrome,
  ProductMockupBody,
  ProductMockupFrame
} from '../AmplifyProductMockups/shared';
import { SkeletonBar } from '../OptimizeProductMockups/shared';

const TwoColumnGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 12px;
  align-items: stretch;
  min-width: 0;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ListPanel = styled('div')`
  min-width: 0;

  @media (max-width: 560px) {
    display: none;
  }
`;

const ListCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 10px 12px;
  overflow: hidden;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
`;

const ListToolbar = styled('div')`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
`;

const ListRow = styled('div')<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 2px;
  border-bottom: 1px solid #f6f6f4;
  min-width: 0;
  background: ${(p) => (p.selected ? '#eef3ff' : 'transparent')};
  border-radius: ${(p) => (p.selected ? '6px' : '0')};

  &:last-child {
    border-bottom: none;
  }
`;

const StatusDot = styled('span')<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const RowText = styled('span')<{ selected?: boolean }>`
  font-size: 10px;
  color: ${(p) => (p.selected ? '#0e1c2b' : '#4f4f4f')};
  font-weight: ${(p) => (p.selected ? 700 : 400)};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const SlugChip = styled('span')<{ size?: 'sm' | 'md' }>`
  font-size: ${(p) => (p.size === 'md' ? '9px' : '8.5px')};
  font-weight: 600;
  color: #2e5cff;
  background: #eef3ff;
  border-radius: 100px;
  padding: ${(p) => (p.size === 'md' ? '3px 10px' : '2px 8px')};
  white-space: nowrap;
  flex-shrink: 0;
  display: ${(p) => (p.size === 'md' ? 'inline-block' : 'inline')};
`;

const DetailPanel = styled('div')`
  min-width: 0;
`;

const DetailCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 6px 24px rgba(14, 28, 43, 0.06);
  min-width: 0;
`;

const Section = styled('div')`
  &:not(:first-child) {
    margin-top: 10px;
  }
`;

const SectionLabel = styled('div')`
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const AmountRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Amount = styled('span')`
  font-size: 22px;
  font-weight: 800;
  color: #0e1c2b;
`;

const StatusPill = styled('span')`
  font-size: 9.5px;
  font-weight: 700;
  color: #d48806;
  background: #fff1b8;
  border-radius: 100px;
  padding: 3px 10px;
`;

const OrderTitle = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: #0e1c2b;
  margin-bottom: 8px;
`;

const KvRow = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 10.5px;
  padding: 3px 0;
  min-width: 0;

  .key {
    color: #94a3b8;
    flex-shrink: 0;
  }

  .value {
    color: #0e1c2b;
    font-weight: 600;
    text-align: right;
    min-width: 0;
  }
`;

const SkeletonKvRow = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
  min-width: 0;
`;

const Timeline = styled('div')`
  display: flex;
  flex-direction: column;
`;

const TimelineEntry = styled('div')<{ last?: boolean }>`
  display: flex;
  gap: 10px;
  padding-bottom: ${(p) => (p.last ? '0' : '8px')};
  min-width: 0;
`;

const TimelineIconColumn = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const TimelineCircle = styled('div')<{ tone: 'dark' | 'muted' }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) => (p.tone === 'dark' ? '#0e1c2b' : '#e2e8f0')};
  color: ${(p) => (p.tone === 'dark' ? '#fff' : '#94a3b8')};
`;

const TimelineConnector = styled('div')`
  width: 2px;
  flex: 1;
  background: #e8ebee;
  margin: 2px auto 0;
  min-height: 10px;
`;

const TimelineContent = styled('div')`
  min-width: 0;
  padding-top: 1px;
`;

const TimelineTitle = styled('div')`
  font-size: 10.5px;
  font-weight: 700;
  color: #0e1c2b;
`;

const TimelineMeta = styled('div')`
  margin-top: 3px;
`;

type ListRowData =
  | {
      dot: string;
      selected: true;
      text: string;
      slug: string;
    }
  | {
      dot: string;
      selected: false;
      textWidth: number;
      slugWidth: number;
    };

const LIST_ROWS: ListRowData[] = [
  { dot: '#a0d911', selected: false, textWidth: 88, slugWidth: 72 },
  { dot: '#fadb14', selected: false, textWidth: 68, slugWidth: 98 },
  { dot: '#f5222d', selected: false, textWidth: 110, slugWidth: 88 },
  { dot: '#a0d911', selected: true, text: 'NordVPN', slug: '/best-vpn' },
  { dot: '#a0d911', selected: false, textWidth: 96, slugWidth: 82 },
  { dot: '#fadb14', selected: false, textWidth: 90, slugWidth: 88 },
  { dot: '#a0d911', selected: false, textWidth: 82, slugWidth: 54 }
];

export const TransactionDetailMockup = () => (
  <ProductMockupFrame aria-hidden>
    <AppMockupChrome product="Unify" surface="Transaction details" />
    <ProductMockupBody>
      <TwoColumnGrid>
        <ListPanel>
          <ListCard>
            <ListToolbar>
              <SkeletonBar w={54} h={16} />
              <SkeletonBar w={72} h={16} />
              <SkeletonBar w={44} h={16} />
            </ListToolbar>
            {LIST_ROWS.map((row, index) => (
              <ListRow key={index} selected={row.selected}>
                <StatusDot color={row.dot} />
                {row.selected ? (
                  <>
                    <RowText selected>{row.text}</RowText>
                    <SlugChip>{row.slug}</SlugChip>
                  </>
                ) : (
                  <>
                    <RowText>
                      <SkeletonBar w={row.textWidth} h={7} />
                    </RowText>
                    <SkeletonBar w={row.slugWidth} h={12} />
                  </>
                )}
              </ListRow>
            ))}
          </ListCard>
        </ListPanel>

        <DetailPanel>
          <DetailCard>
            <Section>
              <SectionLabel>Commission</SectionLabel>
              <AmountRow>
                <Amount>$35.71</Amount>
                <StatusPill>Pending</StatusPill>
              </AmountRow>
              <SkeletonBar w={92} h={6} />
            </Section>

            <Section>
              <SectionLabel>Order details</SectionLabel>
              <OrderTitle>NordVPN</OrderTitle>
              <KvRow>
                <span className="key">Sale amount</span>
                <span className="value">$79.35</span>
              </KvRow>
              <SkeletonKvRow>
                <SkeletonBar w={72} h={7} />
                <SkeletonBar w={28} h={7} />
              </SkeletonKvRow>
              <SkeletonKvRow>
                <SkeletonBar w={56} h={7} />
                <SkeletonBar w={48} h={7} />
              </SkeletonKvRow>
            </Section>

            <Section>
              <SectionLabel>Tracking</SectionLabel>
              <SlugChip size="md">/best-vpn</SlugChip>
            </Section>

            <Section>
              <SectionLabel>Audience</SectionLabel>
              <SkeletonKvRow>
                <SkeletonBar w={64} h={7} />
                <SkeletonBar w={72} h={7} />
              </SkeletonKvRow>
              <SkeletonKvRow>
                <SkeletonBar w={58} h={7} />
                <SkeletonBar w={52} h={7} />
              </SkeletonKvRow>
            </Section>

            <Section>
              <SectionLabel>Timeline</SectionLabel>
              <Timeline>
                <TimelineEntry>
                  <TimelineIconColumn>
                    <TimelineCircle tone="dark">
                      <MousePointerClick size={9} />
                    </TimelineCircle>
                    <TimelineConnector />
                  </TimelineIconColumn>
                  <TimelineContent>
                    <TimelineTitle>Click</TimelineTitle>
                    <TimelineMeta>
                      <SkeletonBar w={118} h={6} />
                    </TimelineMeta>
                  </TimelineContent>
                </TimelineEntry>
                <TimelineEntry last>
                  <TimelineIconColumn>
                    <TimelineCircle tone="dark">
                      <ShoppingCart size={9} />
                    </TimelineCircle>
                  </TimelineIconColumn>
                  <TimelineContent>
                    <TimelineTitle>Conversion</TimelineTitle>
                    <TimelineMeta>
                      <SkeletonBar w={104} h={6} />
                    </TimelineMeta>
                  </TimelineContent>
                </TimelineEntry>
              </Timeline>
            </Section>
          </DetailCard>
        </DetailPanel>
      </TwoColumnGrid>
    </ProductMockupBody>
  </ProductMockupFrame>
);
